import React, { useState, useCallback, useContext } from "react";
import { connect } from "react-redux";
import classNames from "classnames";
import PerfectScrollbar from "react-perfect-scrollbar";
import object from "lodash/object";
import Imgix from "react-imgix";
import SidebarCategory from "./SidebarCategory";
import SidebarItem, { SidebarItemProps } from "./SidebarItem";
import Logo from "../../assets/img/pp-logo-core-white.svg";
import Kebabs from "../../assets/img/icons/icon-kebabs.svg";
import { AppContext, User } from "../../context/app";
import routes, { Route } from "../../routes/routes-list";
import DebugModal from "./DebugModal";
import { getInitials } from "../../utils/misc";
import { useFeatureFlag } from "../../context/features";
import PageSearch from "./components/PageSearch";
import { MenuIconButton } from "@pushpress/shared-components";
import { useExperiment } from "../../hooks/useExperiment";
import {
  SHOW_NEW_PP_PLAN_PAGE_FEATURE_FLAG,
  SHOW_PEOPLE_PAGE_FEATURE_FLAG,
} from "../../context/features/features.utils";

const NEW_CLIENT_TIMESTAMP = process.env.NEW_CLIENT_TIMESTAMP || 1675209600;

const avatarColor = (membershipStatus: User["membershipStatus"]) => {
  switch (membershipStatus) {
    case "admin":
    case "coach":
    case "frontdesk":
    case "lead":
      return "#5bc0de";
    case "member":
      return "#5cb85c";
    case "non-member":
    case "ex-member":
      return "#d9534f";
    default:
      return "#5bc0de";
  }
};

function generateActiveState(routes: Route[], pathname = "") {
  return routes.reduce((result, route, index) => {
    const isActive = pathname.indexOf(route.path) === 0; // check if current path is category path
    const children = object.get(route, "children", []);
    const hasActiveChild = children.some((childRoute) => {
      return pathname.indexOf(childRoute.path) === 0; // check if pathname is child of category
    });
    const subPaths = object.get(route, "subPaths", []);
    const isActiveSubPath = subPaths.some((subPath) => {
      return pathname.indexOf(subPath) === 0;
    });

    return {
      ...result,
      [index]: isActive || hasActiveChild || isActiveSubPath,
    };
  }, {});
}

type SidebarProps = {
  sidebar: {
    isSticky: boolean;
    isOpen: boolean;
  };
};

const Sidebar = ({ sidebar }: SidebarProps) => {
  const { isFeatureEnabled } = useFeatureFlag();
  const { useExperimentValue } = useExperiment();
  const isPeoplePageEnabled = useExperimentValue(
    SHOW_PEOPLE_PAGE_FEATURE_FLAG,
    false,
  ) as boolean;

  const isNewAccountPageEnabled = useExperimentValue(
    SHOW_NEW_PP_PLAN_PAGE_FEATURE_FLAG,
    false,
  ) as boolean;

  const sidebarRoutes = routes({
    features: {
      isPeoplePageEnabled,
      isNewAccountPageEnabled,
    },
  });

  // If has a context serving, use context data, if not, get via window.react variable for embeds.
  const appContext = useContext(AppContext);
  const client = appContext?.client;
  const user = appContext?.user;
  const { pathname } = window.location;
  const { first_name: firstName, last_name: lastName } = user || {};
  const initialState = generateActiveState(sidebarRoutes, pathname);
  const [sidebarState, setSidebarState] = useState<{
    dropdownOpen?: boolean;
  }>(initialState);
  const [showDebugModal, setShowDebugModal] = useState(false);

  const clientJoinTimestamp = client?.joinTimestamp || client?.join_timestamp;

  const isNewClient =
    Number(clientJoinTimestamp) > Number(NEW_CLIENT_TIMESTAMP);

  const toggle = useCallback(
    (index) => {
      // Collapse all elements
      Object.keys(sidebarState).forEach(
        (item) =>
          sidebarState[index] ||
          setSidebarState({ ...sidebarState, [item]: false }),
      );

      // Toggle selected element
      setSidebarState({
        ...sidebarState,
        [index]: !sidebarState[index],
        dropdownOpen: false,
      });
    },
    [setSidebarState, sidebarState],
  );

  const renderSidebarItem = (route: SidebarItemProps) => {
    return <SidebarItem key={route.id} {...route} />;
  };

  const applyCategoryFilter = (category: Route) => {
    const result = category.children?.filter((child) => {
      if (child.path === "/scheduler/index" && isNewClient) {
        return false;
      }

      if (child.featureRule && !isFeatureEnabled(child.featureRule)) {
        // If requires a feature flag, if it's disabled, return false
        return false;
      }

      if (
        child.disabledFeatureRule &&
        isFeatureEnabled(child.disabledFeatureRule)
      ) {
        // If requires a feature flag to be disabled, if it's enabled, return false
        return false;
      }

      return true;
    });

    return result || [];
  };

  const renderSidebarRoutes = (category: Route, index: number) => {
    if (
      (category.featureRule && !isFeatureEnabled(category.featureRule)) ||
      (category.disabledFeatureRule &&
        isFeatureEnabled(category.disabledFeatureRule))
    ) {
      return;
    }
    return (
      <React.Fragment key={index}>
        {category.header ? (
          <li className="sidebar-header">{category.header}</li>
        ) : null}
        {category.children || category.dynamicChildren ? (
          <SidebarCategory
            name={category.name}
            badgeColor={category.badgeColor}
            badgeText={category.badgeText}
            icon={category.icon}
            path={category.path}
            isOpen={sidebarState[index]}
            onClick={() => toggle(index)}
          >
            {category.dynamicChildren?.()}
            {applyCategoryFilter(category).map(renderSidebarItem)}
          </SidebarCategory>
        ) : (
          renderSidebarItem(category)
        )}
      </React.Fragment>
    );
  };

  const userInitials = getInitials(firstName, lastName);

  return (
    <PageSearch>
      <nav
        className={classNames("sidebar", {
          toggled: !sidebar.isOpen,
          "sidebar-sticky": sidebar.isSticky,
        })}
      >
        <div className="sidebar-content">
          <PerfectScrollbar>
            <a className={`sidebar-brand ${appContext.spa && "my-2"}`} href="/">
              <Logo />
            </a>

            <ul className="sidebar-nav">
              {sidebarRoutes.map(renderSidebarRoutes)}
            </ul>

            <div className="sidebar-bottom d-lg-block tw-border-t-blue-500 tw-border-t">
              <div className="media">
                {user?.avatar ? (
                  <Imgix
                    src={user?.avatar}
                    className="rounded-circle mr-3"
                    width={40}
                    height={40}
                    imgixParams={{
                      w: 250,
                      h: 250,
                      fit: "facearea",
                      facepad: 2.5,
                    }}
                  />
                ) : (
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center mr-3"
                    style={{
                      backgroundColor: avatarColor(user?.membershipStatus),
                      width: "40px",
                      height: "40px",
                    }}
                  >
                    {userInitials}
                  </div>
                )}
                <div
                  className="media-body"
                  style={{
                    // required for PHP pages
                    minWidth: "1px",
                  }}
                >
                  <h5 className="mb-1">
                    {firstName} {lastName}
                  </h5>
                  <div>{client.company}</div>
                </div>
                <div style={{ alignSelf: "center" }}>
                  <MenuIconButton
                    icon={Kebabs}
                    menuItems={[
                      {
                        label: "My Profile",
                        onClick: () =>
                          (document.location.href = `/users/edit/${user?.user_uuid}`),
                      },
                      {
                        label: "Logout",
                        onClick: () =>
                          (document.location.href = "/account/logout"),
                      },
                    ]}
                  />
                </div>
              </div>
            </div>
          </PerfectScrollbar>
        </div>
      </nav>
      {showDebugModal && !appContext.spa && (
        <DebugModal closeDebugModal={() => setShowDebugModal(false)} />
      )}
    </PageSearch>
  );
};

function mapStateToProps(state) {
  return {
    sidebar: state.sidebar,
  };
}

export default connect(mapStateToProps)(Sidebar);
